/**
 * Junto Frontend FilterPopup Component
 * 
 * Filter popup for booking system that allows filtering by location,
 * time of day, instructors, class types, and waitlist options.
 * Theme-agnostic and reusable across different studio brands.
 */

import { useState, useEffect } from 'react';
import styled from 'styled-components';
import Select from 'react-select';
import { Modal } from '../ui/index.js';
import { locationService, instructorService, referenceDataService } from '../../api/services.js';
import { solRoomTheme } from '../../themes/sol-room.js';

const FilterContent = styled.div`
  background: ${props => props.theme?.colors?.primary?.background || solRoomTheme.colors.primary.background};
  margin: -32px;
  padding: 32px;
  
  @media (max-width: ${props => props.theme?.breakpoints?.tablet || solRoomTheme.breakpoints.tablet}) {
    margin: -24px;
    padding: 24px;
  }
`;

const FilterTitle = styled.h3`
  font-family: ${props => props.theme?.typography?.primary?.fontFamily || solRoomTheme.typography.primary.fontFamily};
  font-size: 1.5rem;
  font-weight: 400;
  color: ${props => props.theme?.colors?.primary?.text || solRoomTheme.colors.primary.text};
  margin: 0 0 ${props => props.theme?.spacing?.md || solRoomTheme.spacing.md} 0;
  text-align: center;
`;

const FilterSection = styled.div`
  margin-bottom: ${props => props.theme?.spacing?.sm || solRoomTheme.spacing.sm};
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const FilterLabel = styled.label`
  display: block;
  font-family: ${props => props.theme?.typography?.secondary?.fontFamily || solRoomTheme.typography.secondary.fontFamily};
  font-size: 1rem;
  font-weight: 500;
  color: ${props => props.theme?.colors?.primary?.text || solRoomTheme.colors.primary.text};
  margin-bottom: ${props => props.theme?.spacing?.sm || solRoomTheme.spacing.sm};
`;

const TimeOfDayContainer = styled.div`
  display: flex;
  gap: ${props => props.theme?.spacing?.sm || solRoomTheme.spacing.sm};
  flex-wrap: nowrap;
  
  @media (max-width: ${props => props.theme?.breakpoints?.mobile || solRoomTheme.breakpoints.mobile}) {
    gap: ${props => props.theme?.spacing?.xs || solRoomTheme.spacing.xs};
  }
`;

const TimeOption = styled.button`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${props => props.theme?.spacing?.xs || solRoomTheme.spacing.xs};
  background: ${props => props.$selected ? (props.theme?.colors?.accent?.main || solRoomTheme.colors.accent.main) : 'transparent'};
  border: 1px solid ${props => props.$selected ? (props.theme?.colors?.accent?.main || solRoomTheme.colors.accent.main) : (props.theme?.colors?.neutral?.medium || solRoomTheme.colors.neutral.medium)};
  border-radius: 8px;
  padding: ${props => props.theme?.spacing?.sm || solRoomTheme.spacing.sm};
  cursor: pointer;
  transition: ${props => props.theme?.transitions?.default || solRoomTheme.transitions.default};
  flex: 1;
  min-width: 90px;
  
  &:hover {
    background: ${props => props.$selected ? (props.theme?.colors?.accent?.main || solRoomTheme.colors.accent.main) : (props.theme?.colors?.neutral?.light || solRoomTheme.colors.neutral.light)};
  }
  
  @media (max-width: ${props => props.theme?.breakpoints?.mobile || solRoomTheme.breakpoints.mobile}) {
    min-width: 80px;
    padding: ${props => props.theme?.spacing?.xs || solRoomTheme.spacing.xs} ${props => props.theme?.spacing?.sm || solRoomTheme.spacing.sm};
  }
`;

const TimeIcon = styled.div`
  font-size: 1.5rem;
`;

const TimeLabel = styled.span`
  font-family: ${props => props.theme?.typography?.secondary?.fontFamily || solRoomTheme.typography.secondary.fontFamily};
  font-size: 0.8rem;
  color: ${props => props.$selected ? (props.theme?.colors?.primary?.background || solRoomTheme.colors.primary.background) : (props.theme?.colors?.primary?.text || solRoomTheme.colors.primary.text)};
  font-weight: 500;
`;

const CheckboxContainer = styled.div`
  display: flex;
  align-items: center;
  gap: ${props => props.theme?.spacing?.sm || solRoomTheme.spacing.sm};
`;

const Checkbox = styled.input.attrs({ type: 'checkbox' })`
  accent-color: ${props => props.theme?.colors?.accent?.main || solRoomTheme.colors.accent.main};
`;

const CheckboxLabel = styled.label`
  font-family: ${props => props.theme?.typography?.secondary?.fontFamily || solRoomTheme.typography.secondary.fontFamily};
  font-size: 0.9rem;
  color: ${props => props.theme?.colors?.primary?.text || solRoomTheme.colors.primary.text};
  cursor: pointer;
  user-select: none;
`;

const SelectContainer = styled.div`
  width: 100%;
`;

const InstructorSelectContainer = styled.div`
  width: 100%;
  
  /* Fix width for multi-select to prevent layout changes */
  .react-select__control {
    min-width: 280px;
    width: 100%;
  }
  
  .react-select__value-container {
    max-width: 220px;
    overflow: hidden;
  }
  
  .react-select__multi-value {
    max-width: 120px;
  }
  
  .react-select__multi-value__label {
    max-width: 80px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`;

const ErrorMessage = styled.div`
  text-align: center;
  padding: ${props => props.theme?.spacing?.md || solRoomTheme.spacing.md};
  font-family: ${props => props.theme?.typography?.secondary?.fontFamily || solRoomTheme.typography.secondary.fontFamily};
  color: ${props => props.theme?.colors?.accent?.main || solRoomTheme.colors.accent.main};
  font-size: 0.9rem;
`;

const LoadingMessage = styled.div`
  text-align: center;
  padding: ${props => props.theme?.spacing?.md || solRoomTheme.spacing.md};
  font-family: ${props => props.theme?.typography?.secondary?.fontFamily || solRoomTheme.typography.secondary.fontFamily};
  color: ${props => props.theme?.colors?.primary?.text || solRoomTheme.colors.primary.text};
  opacity: 0.7;
  font-size: 0.9rem;
`;

const ButtonContainer = styled.div`
  display: flex;
  justify-content: center;
  gap: ${props => props.theme?.spacing?.md || solRoomTheme.spacing.md};
  margin-top: ${props => props.theme?.spacing?.lg || solRoomTheme.spacing.lg};
`;

const ActionButton = styled.button`
  font-family: ${props => props.theme?.typography?.button?.fontFamily || solRoomTheme.typography.button.fontFamily};
  font-size: calc(${props => props.theme?.typography?.button?.fontSize || solRoomTheme.typography.button.fontSize} * 0.875);
  font-weight: ${props => props.theme?.typography?.button?.fontWeight || solRoomTheme.typography.button.fontWeight};
  letter-spacing: ${props => props.theme?.typography?.button?.letterSpacing || solRoomTheme.typography.button.letterSpacing};
  text-transform: ${props => props.theme?.typography?.button?.textTransform || solRoomTheme.typography.button.textTransform};
  
  background: ${props => props.$primary ? (props.theme?.colors?.primary?.buttons || solRoomTheme.colors.primary.buttons) : 'transparent'};
  color: ${props => props.$primary ? (props.theme?.colors?.primary?.background || solRoomTheme.colors.primary.background) : (props.theme?.colors?.primary?.buttons || solRoomTheme.colors.primary.buttons)};
  border: 1px solid ${props => props.theme?.colors?.primary?.buttons || solRoomTheme.colors.primary.buttons};
  border-radius: 50px;
  
  padding: ${props => props.theme?.spacing?.sm || solRoomTheme.spacing.sm} ${props => props.theme?.spacing?.md || solRoomTheme.spacing.md};
  cursor: pointer;
  transition: ${props => props.theme?.transitions?.default || solRoomTheme.transitions.default};
  text-decoration: none;
  
  &:hover {
    background: ${props => props.theme?.colors?.primary?.buttons || solRoomTheme.colors.primary.buttons};
    color: ${props => props.theme?.colors?.primary?.background || solRoomTheme.colors.primary.background};
    transform: translateY(-2px);
  }
  
  &:active {
    transform: translateY(0);
  }
`;

const getCustomSelectStyles = (theme) => ({
  control: (provided) => ({
    ...provided,
    border: `1px solid ${theme?.colors?.neutral?.medium || solRoomTheme.colors.neutral.medium}`,
    borderRadius: '8px',
    backgroundColor: theme?.colors?.primary?.background || solRoomTheme.colors.primary.background,
    minHeight: '40px',
    boxShadow: 'none',
    '&:hover': {
      borderColor: theme?.colors?.neutral?.medium || solRoomTheme.colors.neutral.medium,
    },
    '&:focus-within': {
      borderColor: theme?.colors?.accent?.main || solRoomTheme.colors.accent.main,
      boxShadow: `0 0 0 1px ${theme?.colors?.accent?.main || solRoomTheme.colors.accent.main}`,
    },
  }),
  menu: (provided) => ({
    ...provided,
    backgroundColor: theme?.colors?.primary?.background || solRoomTheme.colors.primary.background,
    border: `1px solid ${theme?.colors?.neutral?.medium || solRoomTheme.colors.neutral.medium}`,
    borderRadius: '8px',
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
  }),
  option: (provided, state) => ({
    ...provided,
    backgroundColor: state.isSelected 
      ? (theme?.colors?.accent?.main || solRoomTheme.colors.accent.main)
      : state.isFocused 
      ? (theme?.colors?.neutral?.light || solRoomTheme.colors.neutral.light)
      : (theme?.colors?.primary?.background || solRoomTheme.colors.primary.background),
    color: state.isSelected 
      ? (theme?.colors?.primary?.background || solRoomTheme.colors.primary.background)
      : (theme?.colors?.primary?.text || solRoomTheme.colors.primary.text),
    padding: '8px 12px',
    '&:hover': {
      backgroundColor: state.isSelected 
        ? (theme?.colors?.accent?.main || solRoomTheme.colors.accent.main)
        : (theme?.colors?.neutral?.light || solRoomTheme.colors.neutral.light),
    },
  }),
  multiValue: (provided) => ({
    ...provided,
    backgroundColor: theme?.colors?.accent?.bright || solRoomTheme.colors.accent.bright,
    borderRadius: '4px',
  }),
  multiValueLabel: (provided) => ({
    ...provided,
    color: theme?.colors?.primary?.text || solRoomTheme.colors.primary.text,
    fontSize: '0.85rem',
  }),
  multiValueRemove: (provided) => ({
    ...provided,
    color: theme?.colors?.primary?.text || solRoomTheme.colors.primary.text,
    '&:hover': {
      backgroundColor: theme?.colors?.accent?.main || solRoomTheme.colors.accent.main,
      color: theme?.colors?.primary?.background || solRoomTheme.colors.primary.background,
    },
  }),
  placeholder: (provided) => ({
    ...provided,
    color: theme?.colors?.primary?.text || solRoomTheme.colors.primary.text,
    opacity: 0.6,
    fontSize: '0.9rem',
  }),
  singleValue: (provided) => ({
    ...provided,
    color: theme?.colors?.primary?.text || solRoomTheme.colors.primary.text,
    fontSize: '0.9rem',
  }),
});

const timeOptions = [
  { id: 'morning', label: 'Morning', icon: '🌅' },
  { id: 'afternoon', label: 'Afternoon', icon: '☀️' },
  { id: 'evening', label: 'Evening', icon: '🌙' }
];

const FilterPopup = ({ 
  isOpen, 
  onClose, 
  filters, 
  onFiltersChange,
  classType = 'group',
  theme = solRoomTheme
}) => {
  const [localFilters, setLocalFilters] = useState({
    ...filters,
    hideWaitlist: filters.hideWaitlist !== undefined ? filters.hideWaitlist : false
  });

  // State for API data
  const [locationOptions, setLocationOptions] = useState([]);
  const [instructorOptions, setInstructorOptions] = useState([]);
  const [classTypeOptions, setClassTypeOptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Load filter options from real API
  useEffect(() => {
    const loadFilterOptions = async () => {
      try {
        setLoading(true);
        setError(null);

        // Load all filter data in parallel
        const [locations, instructors, referenceData] = await Promise.all([
          locationService.getLocations(),
          instructorService.getInstructors(), 
          referenceDataService.getReferenceData()
        ]);

        // Process locations - sort alphabetically
        const locationSelectOptions = locations
          .sort((a, b) => a.name.localeCompare(b.name))
          .map(location => ({
            value: location.id,
            label: location.name
          }));
        setLocationOptions(locationSelectOptions);

        // Process instructors - use exactly what Junto Pay API v2 returns
        const instructorSelectOptions = instructors.map(instructor => ({
          value: instructor.id,
          label: `${instructor.first_name} ${instructor.last_name}`.trim()
        }));
        setInstructorOptions(instructorSelectOptions);

        // Process class categories using event_type field
        const categories = referenceData.categories;
        
        // Filter categories based on event_type field (not heuristic)
        const eventTypeMap = {
          'group': 'group',
          'private': 'private', 
          'events': 'event'
        };
        
        const targetEventType = eventTypeMap[classType];
        const filteredCategories = categories.filter(category => 
          category.event_type === targetEventType
        );

        const classTypeSelectOptions = filteredCategories.map(category => ({
          value: category.id,
          label: category.name
        }));

        setClassTypeOptions(classTypeSelectOptions);

        // Set smart defaults if no filters are currently set
        const shouldSetDefaults = !localFilters.location && localFilters.hours.length === 0;
        
        if (shouldSetDefaults && locationSelectOptions.length > 0) {
          const defaultFilters = {
            ...localFilters,
            location: locationSelectOptions[0].value,  // First location
            hours: ['morning', 'afternoon', 'evening']  // All time options
          };
          setLocalFilters(defaultFilters);
          onFiltersChange(defaultFilters);
        }

      } catch (err) {
        console.error('Failed to load filter options:', err);
        setError('Failed to load filter options');
      } finally {
        setLoading(false);
      }
    };

    if (isOpen) {
      loadFilterOptions();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, classType]);

  // Update local filters when filters prop changes
  useEffect(() => {
    setLocalFilters({
      ...filters,
      hideWaitlist: filters.hideWaitlist !== undefined ? filters.hideWaitlist : false
    });
  }, [filters]);

  const handleLocationChange = (selectedOption) => {
    const newFilters = {
      ...localFilters,
      location: selectedOption ? selectedOption.value : ''
    };
    setLocalFilters(newFilters);
    onFiltersChange(newFilters);
  };

  const handleTimeToggle = (timeId) => {
    const newHours = localFilters.hours.includes(timeId)
      ? localFilters.hours.filter(h => h !== timeId)
      : [...localFilters.hours, timeId];
    
    const newFilters = {
      ...localFilters,
      hours: newHours
    };
    setLocalFilters(newFilters);
    onFiltersChange(newFilters);
  };

  const handleInstructorChange = (selectedOptions) => {
    const newInstructors = selectedOptions ? selectedOptions.map(option => option.value) : [];
    const newFilters = {
      ...localFilters,
      instructors: newInstructors
    };
    setLocalFilters(newFilters);
    onFiltersChange(newFilters);
  };

  const handleClassTypeChange = (selectedOptions) => {
    const newClassTypes = selectedOptions ? selectedOptions.map(option => option.value) : [];
    const newFilters = {
      ...localFilters,
      classTypes: newClassTypes
    };
    setLocalFilters(newFilters);
    onFiltersChange(newFilters);
  };

  const handleHideWaitlistChange = (e) => {
    const newFilters = {
      ...localFilters,
      hideWaitlist: e.target.checked
    };
    setLocalFilters(newFilters);
    onFiltersChange(newFilters);
  };

  const handleReset = () => {
    const defaultFilters = {
      location: locationOptions.length > 0 ? locationOptions[0].value : '',  // First location (alphabetically)
      hours: ['morning', 'afternoon', 'evening'],  // All time options
      instructors: [],
      classTypes: [],
      hideWaitlist: false
    };
    setLocalFilters(defaultFilters);
    onFiltersChange(defaultFilters);
  };

  const customSelectStyles = getCustomSelectStyles(theme);

  if (loading) {
    return (
      <Modal isOpen={isOpen} onClose={onClose} maxWidth="380px" theme={theme}>
        <FilterContent theme={theme}>
          <FilterTitle theme={theme}>Filter Classes</FilterTitle>
          <LoadingMessage theme={theme}>Loading filter options...</LoadingMessage>
        </FilterContent>
      </Modal>
    );
  }

  if (error) {
    return (
      <Modal isOpen={isOpen} onClose={onClose} maxWidth="380px" theme={theme}>
        <FilterContent theme={theme}>
          <FilterTitle theme={theme}>Filter Classes</FilterTitle>
          <ErrorMessage theme={theme}>{error}</ErrorMessage>
          <ButtonContainer theme={theme}>
            <ActionButton onClick={onClose} $primary theme={theme}>
              Close
            </ActionButton>
          </ButtonContainer>
        </FilterContent>
      </Modal>
    );
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} maxWidth="380px" theme={theme}>
      <FilterContent theme={theme}>
        <FilterTitle theme={theme}>Filter Classes</FilterTitle>
        
        <FilterSection theme={theme}>
          <FilterLabel theme={theme}>Location</FilterLabel>
          <SelectContainer>
            <Select
              value={locationOptions.find(option => option.value === localFilters.location)}
              onChange={handleLocationChange}
              options={locationOptions}
              styles={customSelectStyles}
              placeholder="Select location..."
              isClearable
            />
          </SelectContainer>
        </FilterSection>

        <FilterSection theme={theme}>
          <FilterLabel theme={theme}>Time of Day</FilterLabel>
          <TimeOfDayContainer theme={theme}>
            {timeOptions.map(time => (
              <TimeOption
                key={time.id}
                theme={theme}
                $selected={localFilters.hours.includes(time.id)}
                onClick={() => handleTimeToggle(time.id)}
              >
                <TimeIcon>{time.icon}</TimeIcon>
                <TimeLabel theme={theme} $selected={localFilters.hours.includes(time.id)}>
                  {time.label}
                </TimeLabel>
              </TimeOption>
            ))}
          </TimeOfDayContainer>
        </FilterSection>

        <FilterSection theme={theme}>
          <FilterLabel theme={theme}>Instructors</FilterLabel>
          <InstructorSelectContainer>
            <Select
              isMulti
              value={instructorOptions.filter(option => 
                localFilters.instructors.includes(option.value)
              )}
              onChange={handleInstructorChange}
              options={instructorOptions}
              styles={customSelectStyles}
              placeholder="Select instructors..."
              isClearable
              closeMenuOnSelect={false}
            />
          </InstructorSelectContainer>
        </FilterSection>

        <FilterSection theme={theme}>
          <FilterLabel theme={theme}>Class Types</FilterLabel>
          <SelectContainer>
            <Select
              isMulti
              value={classTypeOptions.filter(option => 
                localFilters.classTypes.includes(option.value)
              )}
              onChange={handleClassTypeChange}
              options={classTypeOptions}
              styles={customSelectStyles}
              placeholder="Select class types..."
              isClearable
              closeMenuOnSelect={false}
            />
          </SelectContainer>
        </FilterSection>

        <FilterSection theme={theme}>
          <CheckboxContainer theme={theme}>
            <Checkbox
              id="hideWaitlist"
              checked={localFilters.hideWaitlist}
              onChange={handleHideWaitlistChange}
              theme={theme}
            />
            <CheckboxLabel htmlFor="hideWaitlist" theme={theme}>
              Hide waitlist options
            </CheckboxLabel>
          </CheckboxContainer>
        </FilterSection>

        <ButtonContainer theme={theme}>
          <ActionButton onClick={handleReset} theme={theme}>
            Reset Filters
          </ActionButton>
          <ActionButton onClick={onClose} $primary theme={theme}>
            Close
          </ActionButton>
        </ButtonContainer>
      </FilterContent>
    </Modal>
  );
};

export default FilterPopup;